import { memo } from 'react';
import { Tooltip } from 'antd';
import { EditOutlined, CloseOutlined } from '@ant-design/icons';
import ConditionalRender from '../../../../../shared/components/ConditionalRender';
import TruncatedText from '../../../../../shared/components/TruncatedText';
import { STATISTIC_UNIT_LABELS, STATISTIC_VALUE_MAX_DECIMALS } from '../../constants';
import type { AssetStatistic } from '../../../../../types/InfrastructureManagerTypes';
import styles from './assetStatisticWidget.module.scss';

interface AssetStatisticWidgetProps {
  statistic: AssetStatistic;
  isSelected: boolean;
  canManage: boolean;
  onSelect: (id: number) => void;
  onEdit: (id: number) => void;
  onRemove: (id: number) => void;
}

const formatValue = ({ value, unit }: AssetStatistic) => {
  if (value === null) return '—';
  const formatted = value.toLocaleString(undefined, { maximumFractionDigits: STATISTIC_VALUE_MAX_DECIMALS });
  return unit ? `${formatted} ${STATISTIC_UNIT_LABELS[unit]}` : formatted;
};

const AssetStatisticWidget = ({ statistic, isSelected, canManage, onSelect, onEdit, onRemove }: AssetStatisticWidgetProps) => {
  // A broken widget (its layer or field was removed) can't filter, but can still be removed.
  const isBroken = !!statistic.error;

  const handleSelect = () => onSelect(statistic.id);
  const handleEdit = () => onEdit(statistic.id);
  const handleRemove = () => onRemove(statistic.id);

  const widgetClassName = `${styles.widget} ${isSelected ? styles.selected : ''} ${isBroken ? styles.broken : ''}`;

  return (
    <Tooltip title={statistic.error?.message}>
      <div className={widgetClassName}>
        <button type="button" className={styles.selectArea} onClick={handleSelect} disabled={isBroken} aria-pressed={isSelected}>
          <img src={statistic.iconUrl} alt="" loading="lazy" className={styles.icon} />
          <span className={styles.textGroup}>
            <span className={styles.value}>
              <TruncatedText text={formatValue(statistic)} />
            </span>
            <Tooltip title={statistic.title}>
              <span className={styles.label}>{statistic.title}</span>
            </Tooltip>
          </span>
        </button>
        <ConditionalRender visible={canManage}>
          <div className={styles.hoverActions}>
            <ConditionalRender visible={!isBroken}>
              <button type="button" className={styles.actionButton} onClick={handleEdit} aria-label={`Edit ${statistic.title}`}>
                <EditOutlined />
              </button>
            </ConditionalRender>
            <button
              type="button"
              className={`${styles.actionButton} ${styles.closeIcon}`}
              onClick={handleRemove}
              aria-label={`Remove ${statistic.title}`}
            >
              <CloseOutlined />
            </button>
          </div>
        </ConditionalRender>
      </div>
    </Tooltip>
  );
};

export default memo(AssetStatisticWidget);
